import React from 'react';
import { motion } from 'framer-motion';
import Tilt from 'react-parallax-tilt';
import { 
  EnvelopeIcon, 
  PhoneIcon, 
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import NET from 'vanta/dist/vanta.net.min';
import SEO from '../components/SEO';

function ContactCard({ icon: Icon, title, description, link, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <Tilt tiltMaxAngleX={10} tiltMaxAngleY={10} scale={1.05}>
        <a 
          href={link}
          className="glass p-8 rounded-modern text-center hover:bg-white/5 transition-all duration-300 h-full min-h-[300px] flex flex-col items-center justify-center block"
        >
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-gradient-to-br from-[#2563EB] to-[#4F46E5] rounded-full w-16 h-16 flex items-center justify-center glow">
              <Icon className="w-8 h-8 text-white" />
            </div>
          </div>
          <h3 className="text-2xl font-semibold mb-4 text-white text-center">{title}</h3>
          <p className="text-white/80 text-center text-lg">{description}</p>
        </a>
      </Tilt>
    </motion.div>
  );
}

function Contact() {
  const [vantaEffect, setVantaEffect] = React.useState(null);
  const vantaRef = React.useRef(null);
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const mailtoLink = `mailto:info@remuvr.com?subject=Contact Form Submission from ${formData.name}&body=Name: ${formData.name}%0D%0AEmail: ${formData.email}%0D%0A%0D%0AMessage:%0D%0A${formData.message}`;
    window.location.href = mailtoLink;
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  React.useEffect(() => {
    if (!vantaEffect) {
      setVantaEffect(
        NET({
          el: vantaRef.current,
          mouseControls: true,
          touchControls: true,
          gyroControls: false,
          minHeight: 200.00,
          minWidth: 200.00,
          scale: 1.00,
          scaleMobile: 1.00,
          color: 0x3b82f6,
          backgroundColor: 0x0a1930,
          points: 20.00,
          maxDistance: 15.00,
          spacing: 15.00,
        })
      );
    }
    return () => {
      if (vantaEffect) vantaEffect.destroy();
    };
  }, [vantaEffect]);

  const contactMethods = [
    {
      icon: EnvelopeIcon,
      title: "Email Us",
      description: "Get in touch via email for any inquiries",
      link: "mailto:info@remuvr"
    },
    {
      icon: PhoneIcon,
      title: "Call Us",
      description: "Speak directly with our support team",
      link: "tel:+1234567890"
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: "Live Chat",
      description: "Coming Soon",
      link: "#chat"
    }
  ];

  return (
    <>
      <SEO 
        title="Contact Remuvr | 24/7 Customer Support"
        description="Get in touch with Remuvr's support team for any questions about our AI background removal tool. Quick response times, dedicated customer service, and technical support available 24/7. We're here to help!"
        url="https://remuvr.com/contact"
        pageName="contact"
        type="website"
      />
      <div className="min-h-screen bg-gradient-to-b from-[#1E40AF] to-[#1E3A8A] text-white" ref={vantaRef}>
        <div className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Get in Touch</h1>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              We're here to help! Choose your preferred method of contact below.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {contactMethods.map((method, index) => (
              <ContactCard key={index} {...method} index={index} />
            ))}
          </div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="max-w-2xl mx-auto"
          >
            <div className="glass p-8 rounded-modern">
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <label className="block text-white mb-2" htmlFor="name">Name</label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full p-3 rounded-modern bg-white/10 border border-white/20 text-white focus:outline-none focus:border-[#4F46E5]"
                    placeholder="Your name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white mb-2" htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full p-3 rounded-modern bg-white/10 border border-white/20 text-white focus:outline-none focus:border-[#4F46E5]"
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white mb-2" htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    rows="4"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full p-3 rounded-modern bg-white/10 border border-white/20 text-white focus:outline-none focus:border-[#4F46E5]"
                    placeholder="Your message"
                    required
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full py-3 px-6 bg-gradient-to-br from-[#2563EB] to-[#4F46E5] text-white rounded-modern hover:opacity-90 transition-all duration-300"
                >
                  Send Message
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}

export default Contact;
